import { BinaryFilter, Filter, LogicalOrFilter } from '@cubejs-client/core';

type OptionNode = {
  name: string;
  level: number;
  parent?: string;
  children: string[];
  selected: boolean;
  childrenCount: number;
  selectedChildrenCount: number;
  hasSelectedSiblings: boolean;
  isRoot: boolean;
  parentSelected: boolean;
  isOnlyChild: boolean;
  allDescendantsSelected: boolean;
  selectedSiblings: string[];
  hasSelectedDescendant: boolean;
  isActive: boolean;
};

const DEFAULT_DIMENSIONS = [
  'classification.global_therapeutic_area',
  'classification.global_level_1_indication_name',
  'classification.global_level_2_indication_name',
  'classification.global_level_3_indication_name',
  'classification.global_level_4_indication_name',
];

const createOptionNode = (name: string, level: number, parent?: string, isRoot = false): OptionNode => ({
  name,
  level,
  parent,
  children: [],
  selected: false,
  childrenCount: 0,
  selectedChildrenCount: 0,
  hasSelectedSiblings: false,
  selectedSiblings: [],
  isRoot,
  parentSelected: false,
  isOnlyChild: false,
  allDescendantsSelected: false,
  hasSelectedDescendant: false,
  isActive: false,
});

const buildSelectionLookup = (options: string[], selected: string[]): Record<string, OptionNode> => {
  const lookup: Record<string, OptionNode> = {};
  const selectedSet = new Set(selected);

  options.forEach(opt => {
    const segments = opt.split('__');
    const level = segments.length - 1;
    const parent = level > 0 ? segments.slice(0, -1).join('__') : undefined;
    const isRoot = level === 0;

    if (!lookup[opt]) {
      lookup[opt] = createOptionNode(opt, level, parent, isRoot);
    }

    if (parent && !lookup[parent]) {
      const grandParent = level > 1 ? segments.slice(0, -2).join('__') : undefined;
      lookup[parent] = createOptionNode(parent, level - 1, grandParent, level - 1 === 0);
    }

    if (parent) lookup[parent].children.push(opt);
  });

  selectedSet.forEach(sel => {
    if (lookup[sel]) lookup[sel].selected = true;
  });

  Object.values(lookup).forEach(node => {
    node.childrenCount = node.children.length;
    node.selectedChildrenCount = node.children.filter(child => lookup[child]?.selected).length;
  });

  Object.values(lookup).forEach(node => {
    if (!node.parent) return;
    const parentNode = lookup[node.parent];
    node.parentSelected = parentNode.selected;
    const siblings = parentNode.children.filter(child => child !== node.name);
    node.isOnlyChild = siblings.length === 0;
    node.selectedSiblings = siblings.filter(sibling => lookup[sibling]?.selected);
    node.hasSelectedSiblings = node.selectedSiblings.length > 0;
  });

  const nodesByDescendingLevel = Object.values(lookup).sort((a, b) => b.level - a.level);

  nodesByDescendingLevel.forEach(node => {
    node.hasSelectedDescendant = node.children.some(
      child => lookup[child]?.selected || lookup[child]?.hasSelectedDescendant,
    );

    if (node.children.length === 0) {
      node.allDescendantsSelected = node.selected;
    } else {
      node.allDescendantsSelected = node.children.every(child => lookup[child]?.allDescendantsSelected);
    }

    node.isActive = node.selected || node.hasSelectedDescendant;
  });

  return lookup;
};

const getValue = (node: OptionNode): string => node.name.split('__').pop()!;

const equalsFilter = (node: OptionNode, dimensions: string[]): BinaryFilter => ({
  member: dimensions[node.level],
  operator: 'equals',
  values: [getValue(node)],
});

const naNotSetForLevel = (level: number, dimensions: string[]): LogicalOrFilter => ({
  or: [
    { member: dimensions[level], operator: 'equals', values: ['N/A'] },
    { member: dimensions[level], operator: 'notSet' },
  ],
});

function buildNodeFilter(
  node: OptionNode,
  lookup: Record<string, OptionNode>,
  consumed: Set<string>,
  dimensions: string[],
): Filter | undefined {
  const base = equalsFilter(node, dimensions);

  if (node.allDescendantsSelected && node.selected) {
    consumed.add(node.name);
    return { and: [base] };
  }

  const activeChildren = node.children
    .map(child => lookup[child])
    .filter(child => child?.isActive && !consumed.has(child.name));

  if (activeChildren.length === 0) {
    const filters: Filter[] = [base];

    if (
      node.selected &&
      node.childrenCount > 0 &&
      (node.selectedChildrenCount === 0 || node.selectedChildrenCount === 1)
    ) {
      filters.push(naNotSetForLevel(node.level + 1, dimensions));
    }

    return { and: filters };
  }

  if (activeChildren.length > 1) {
    const byLevel: Record<number, OptionNode[]> = {};

    activeChildren.forEach(activeChild => {
      byLevel[activeChild.level] ||= [];
      byLevel[activeChild.level].push(activeChild);
    });

    const childrenFilters: Filter[] = [];

    Object.values(byLevel).forEach(siblings => {
      const toCollapse: OptionNode[] = [];
      const toRecurse: OptionNode[] = [];

      siblings.forEach(sibling => {
        if (sibling.selected && sibling.allDescendantsSelected) {
          toCollapse.push(sibling);
        } else {
          toRecurse.push(sibling);
        }
      });

      if (toCollapse.length > 0) {
        toCollapse.forEach(siblingToCollapse => consumed.add(siblingToCollapse.name));
        childrenFilters.push({
          member: dimensions[toCollapse[0].level],
          operator: 'equals',
          values: toCollapse.map(childrenToCollapse => childrenToCollapse.name.split('__').at(-1)!),
        });
      }

      toRecurse.forEach(sibling => {
        const f = buildNodeFilter(sibling, lookup, consumed, dimensions);
        if (f) childrenFilters.push(f);
      });
    });

    if (childrenFilters.length > 1) {
      return { and: [base, { or: childrenFilters }] };
    }

    return { and: [base, ...childrenFilters] };
  }

  const child = activeChildren[0];
  const childFilter = buildNodeFilter(child, lookup, consumed, dimensions);

  if (node.selected && child.children.length === 0 && childFilter) {
    return {
      and: [
        base,
        {
          or: [childFilter, naNotSetForLevel(node.level + 1, dimensions)],
        },
      ],
    };
  }

  if (!childFilter) return { and: [base] };

  return {
    and: [base, childFilter],
  };
}

export const parseTaIndicationFilter = (
  selected: string[],
  options: string[],
  dimensions: string[] = DEFAULT_DIMENSIONS,
): Filter | undefined => {
  if (!selected.length) return undefined;

  const lookup = buildSelectionLookup(options, selected);
  const consumed = new Set<string>();

  const activeRootsSet = new Set<OptionNode>();
  for (const sel of selected) {
    let node = lookup[sel];
    if (!node) continue;

    while (node.parent && lookup[node.parent]) {
      node = lookup[node.parent];
    }

    activeRootsSet.add(node);
  }

  const activeRoots = Array.from(activeRootsSet).sort((a, b) => a.level - b.level);

  if (!activeRoots.length) return undefined;

  const filters = activeRoots
    .map(root => buildNodeFilter(root, lookup, consumed, dimensions))
    .filter(filterFromRoot => filterFromRoot !== undefined);

  return filters.length === 1 ? filters[0] : { or: filters };
};
